import React, { useState, useEffect } from 'react';
import { PageHeader, Card, Input, Button, LanguageTabs, Loading } from '../components/ui';
import { useApi } from '../hooks/useApi';
import { useToast } from '../context/ToastContext';

interface HeroContent {
  title_en: string;
  title_ro: string;
  subtitle_en: string;
  subtitle_ro: string;
  cta_primary_en: string;
  cta_primary_ro: string;
  cta_secondary_en: string;
  cta_secondary_ro: string;
  scroll_text_en: string;
  scroll_text_ro: string;
}

const HeroPage: React.FC = () => {
  const { data, loading, error, fetch, update } = useApi<HeroContent>('/api/content/hero');
  const { showToast } = useToast();
  const [lang, setLang] = useState<'en' | 'ro'>('en');
  const [form, setForm] = useState<HeroContent>({
    title_en: '',
    title_ro: '',
    subtitle_en: '',
    subtitle_ro: '',
    cta_primary_en: '',
    cta_primary_ro: '',
    cta_secondary_en: '',
    cta_secondary_ro: '',
    scroll_text_en: '',
    scroll_text_ro: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    if (data) {
      setForm({
        title_en: data.title_en || '',
        title_ro: data.title_ro || '',
        subtitle_en: data.subtitle_en || '',
        subtitle_ro: data.subtitle_ro || '',
        cta_primary_en: data.cta_primary_en || '',
        cta_primary_ro: data.cta_primary_ro || '',
        cta_secondary_en: data.cta_secondary_en || '',
        cta_secondary_ro: data.cta_secondary_ro || '',
        scroll_text_en: data.scroll_text_en || '',
        scroll_text_ro: data.scroll_text_ro || '',
      });
    }
  }, [data]);

  const handleChange = (field: keyof HeroContent) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    setSaving(true);
    const result = await update(form);
    setSaving(false);

    if (result) {
      showToast('Hero section saved!', 'success');
    } else {
      showToast('Failed to save changes', 'error');
    }
  };

  if (loading && !data) {
    return <Loading />;
  }

  return (
    <>
      <PageHeader
        title="Hero Section"
        subtitle="Edit the main landing section of your portfolio"
        actions={
          <Button onClick={handleSave} loading={saving}>
            Save Changes
          </Button>
        }
      />

      <div className="admin-content">
        {error && (
          <div className="admin-login-error" style={{ marginBottom: 20 }}>
            {error}
          </div>
        )}

        <Card title="Hero Content">
          <LanguageTabs currentLang={lang} onChange={setLang} />

          <div className="admin-form">
            {lang === 'en' ? (
              <>
                <Input
                  label="Title (EN)"
                  value={form.title_en}
                  onChange={handleChange('title_en')}
                  placeholder="Welcome to my portfolio"
                />
                <Input
                  label="Subtitle (EN)"
                  value={form.subtitle_en}
                  onChange={handleChange('subtitle_en')}
                  placeholder="Optional subtitle text"
                />
                <div className="admin-form-row">
                  <Input
                    label="Primary CTA (EN)"
                    value={form.cta_primary_en}
                    onChange={handleChange('cta_primary_en')}
                    placeholder="Explore My Universe"
                  />
                  <Input
                    label="Secondary CTA (EN)"
                    value={form.cta_secondary_en}
                    onChange={handleChange('cta_secondary_en')}
                    placeholder="What I Do"
                  />
                </div>
                <Input
                  label="Scroll Text (EN)"
                  value={form.scroll_text_en}
                  onChange={handleChange('scroll_text_en')}
                  placeholder="Scroll to explore"
                />
              </>
            ) : (
              <>
                <Input
                  label="Title (RO)"
                  value={form.title_ro}
                  onChange={handleChange('title_ro')}
                  placeholder="Bun venit în portofoliul meu"
                />
                <Input
                  label="Subtitle (RO)"
                  value={form.subtitle_ro}
                  onChange={handleChange('subtitle_ro')}
                  placeholder="Text subtitrare opțional"
                />
                <div className="admin-form-row">
                  <Input
                    label="Primary CTA (RO)"
                    value={form.cta_primary_ro}
                    onChange={handleChange('cta_primary_ro')}
                    placeholder="Explorează Universul Meu"
                  />
                  <Input
                    label="Secondary CTA (RO)"
                    value={form.cta_secondary_ro}
                    onChange={handleChange('cta_secondary_ro')}
                    placeholder="Ce Fac"
                  />
                </div>
                <Input
                  label="Scroll Text (RO)"
                  value={form.scroll_text_ro}
                  onChange={handleChange('scroll_text_ro')}
                  placeholder="Derulează pentru a explora"
                />
              </>
            )}
          </div>
        </Card>
      </div>
    </>
  );
};

export default HeroPage;
